<template>
  <div v-if="store" class="flex flex-col gap-8 md:gap-6">
    <p v-if="store.showTitle()" data-testid="addressComponentTitle" class="text-input-title">
      {{ props.addressTitle.value }}
    </p>
    <OverseasOption
      v-if="store.config?.allowOverseasAddress"
      :defaultIsOverseasOption="defaultIsOverseasOption"
      :instanceIdentifier="identifier"
      :overseasOptionLabel="overseasOptionLabel?.value"
      :overseasOptionTooltip="overseasOptionTooltip?.value"
      @addressReset="() => onAddressReset()"
    />

    <AddressLookup
      :bindings="addressLookupAutoProps"
      :defaultIsOverseasOption="defaultIsOverseasOption"
      :streetNoRequired="streetNoRequired?.value"
      :streetRequired="streetRequired?.value"
      :suburbRequired="suburbRequired?.value"
      :postcodeRequired="postcodeRequired?.value"
      :countryRequired="countryRequired?.value"
      :checkAddressRequired="checkAddressRequired?.value"
      :selectSuburbRequired="selectSuburbRequired?.value"
      :stateRequired="stateRequired?.value"
      :postcodeInvalid="postcodeInvalid?.value"
      :postalSameAsInsuredAddressLabel="postalSameAsInsuredAddressLabel?.value"
      :invalidInsuranceRiskSuburbMessageTitle="invalidInsuranceRiskSuburbMessageTitle?.value"
      :invalidInsuranceRiskSuburbMessageText="invalidInsuranceRiskSuburbMessageText?.value"
      :riskAddressNotFullyVerifiedMessage="riskAddressNotFullyVerifiedMessage?.value"
      :riskAddressCycloneCheckFailedMessage="riskAddressCycloneCheckFailedMessage?.value"
      :instanceIdentifier="identifier"
      :postalAddressTooltipVariant="postalAddressTooltipVariant?.value"
      :riskAddressTooltipVariant="riskAddressTooltipVariant?.value"
      :physicalAddressTooltipVariant="physicalAddressTooltipVariant?.value"
      :throwJpForNonQldAddress="throwJpForNonQldAddress"
      @addressSelected="(v: RACQAddressResult) => onAddressSelected(v)"
    />
    <ManualAddressForm
      :name="name"
      :streetNoRequired="streetNoRequired?.value"
      :streetRequired="streetRequired?.value"
      :suburbRequired="suburbRequired?.value"
      :postcodeRequired="postcodeRequired?.value"
      :countryRequired="countryRequired?.value"
      :checkAddressRequired="checkAddressRequired?.value"
      :selectSuburbRequired="selectSuburbRequired?.value"
      :stateRequired="stateRequired?.value"
      :invalidInsuranceRiskSuburbMessageTitle="invalidInsuranceRiskSuburbMessageTitle?.value"
      :invalidInsuranceRiskSuburbMessageText="invalidInsuranceRiskSuburbMessageText?.value"
      :riskAddressNotFullyVerifiedMessage="riskAddressNotFullyVerifiedMessage?.value"
      :riskAddressCycloneCheckFailedMessage="riskAddressCycloneCheckFailedMessage?.value"
      :riskSuburbsLabel="riskSuburbsLabel?.value"
      :instanceIdentifier="identifier"
      :noPostcodeMatchesFoundMessage="noPostcodeMatchesFoundMessage?.value"
      :invalidCharactersRegex="validationRegex?.value"
      :invalidCharactersMessage="validationRegexErrorMessage?.value"
      :postcodeInvalid="postcodeInvalid?.value"
      :throwJpForNonQldAddress="throwJpForNonQldAddress"
      :physicalAddressOnlyPOBoxInvalidMessage="physicalAddressOnlyPOBoxInvalidMessage?.value"
      @manualAddressSelected="(v: RACQAddressResult) => onManualAddressSelected(v)"
    />

    <FormKit v-model="activeAddressComponent" type="hidden" :name="formInputId.value" />
  </div>
</template>
<script setup lang="ts">
import { watch, ref, onMounted, computed } from 'vue';
import OverseasOption from './OverseasOption.vue';
import AddressLookup from './AddressLookup.vue';
import ManualAddressForm from './ManualAddressForm.vue';
import { AddressLookupOptionsProps, RACQAddressResult } from '@/interfaces';
import {
  addressLookupEmits,
  emitManualAddressSelected,
  emitAddressSelected,
  useMultiInstanceAddressOptionsStore,
  emitAddressReset,
} from '@/store';
import { convertSitecoreFormFieldToVueFormField } from '@/composables/useFormInputData';
import { v4 } from 'uuid';
import { toSafeDisplayPostcode } from '@/utils/convert';
import { AUSTRALIA, AUSTRALIA_ISO } from '@/constants/general-words.const';

const emit = defineEmits(addressLookupEmits);

const props = defineProps<AddressLookupOptionsProps>();

defineOptions({
  inheritAttrs: false,
});

const addressLookupAutoProps = convertSitecoreFormFieldToVueFormField(props as any, props.formInputId.value);
const multiInstanceAddressStore = useMultiInstanceAddressOptionsStore();
const activeAddressComponent = ref<Partial<RACQAddressResult>>({});
const identifier = ref(props.instanceIdentifier || v4());
const store = computed(() =>
  multiInstanceAddressStore.itemIds.includes(identifier.value)
    ? multiInstanceAddressStore.getInstance(identifier.value)
    : undefined
);

onMounted(() => {
  if (!store.value) multiInstanceAddressStore.addInstance(identifier.value);
  if (props.config)
    store.value?.setupAddressComponent(props.config, props.initialInsuredAddress, props.initialPostalAddress);
});

defineExpose({
  getStore: () => store.value,
});

/*
Decision maker as to whether auto or manual address is used.
*/
watch(
  () => store.value?.useAddress,
  async (newValue) => {
    const newAddress = newValue as RACQAddressResult;
    if (!store.value) return;
    if (store.value.isValidAddress(newAddress)) {
      if (!newValue?.dpid) {
        newAddress.postcode = toSafeDisplayPostcode(newAddress.postcode);
        store.value.setManualAddress(newAddress);
        //When manual address set the address line to 'Not on list...'
        store.value.autoAddressComponents = {
          addressLine: props?.postContent?.value,
        };
      } else {
        store.value.setAutoAddress(newAddress);
      }
    } else {
      store.value.isOverseasSelection = props.defaultIsOverseasOption;
    }
  },
  { once: true }
);

function onManualAddressSelected(value: Partial<RACQAddressResult>) {
  if (!value?.country) value!.country = AUSTRALIA;
  if (!value?.countryISO) value!.countryISO = AUSTRALIA_ISO;

  activeAddressComponent.value = value;
  emit(emitManualAddressSelected, value);
  if (!store.value) return;
  store.value.manualEditEnabled = false;
}

function onAddressSelected(value: Partial<RACQAddressResult>) {
  activeAddressComponent.value = value;
  emit(emitAddressSelected, value);
}

function onAddressReset() {
  activeAddressComponent.value = {};
  emit(emitAddressReset);
}
</script>
